import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Select, Icon, useTheme, colors } from '@iq/react-components';
import { isValid } from 'date-fns';

const isValidTime = (value, timeFormat) => {
  const date = new Date(0);
  switch (timeFormat) {
    case 'HH': {
      date.setHours(value);
      return isValid(date);
    }
    case 'mm': {
      date.setMinutes(value);
      return isValid(date);
    }
    case 'ss': {
      date.setMinutes(value);
      return isValid(date);
    }
    default: {
      return false;
    }
  }
};
const onKeyDown = (event) => {
  if (
    !(
      (Number.parseInt(event.key, 10) >= 0 && event.key >= 0 && event.key <= 9) ||
      event.key === 'Backspace' ||
      event.key === 'Tab'
    )
  ) {
    event.preventDefault();
  }
};

const filterOption = ({ value: option }, inputValue) => option.startsWith(inputValue);

const Picker = ({
  timeFormat,
  value,
  placeholder = '00',
  className,
  onSelect = () => null,
  onFocus = () => null,
  focus,
  min = 0,
  max = 59,
  step = 1,
  styles = {},
  inputRef: inputReference = null,
  noOptionsMessage = 'Invalid time',
  testId,
  ...extraProperties
}) => {
  const theme = useTheme();

  const internalRef = useRef();
  const finalInputRef = inputReference || internalRef;
  const [selectedValue, setSelectedValue] = useState();

  useEffect(() => {
    if (finalInputRef && finalInputRef.current) {
      if (focus) {
        finalInputRef.current.focus();
        document.addEventListener('keydown', onKeyDown, true);
      } else {
        finalInputRef.current.blur();
        document.removeEventListener('keydown', onKeyDown, true);
      }
    }
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [focus, finalInputRef]);

  const handleChange = (selection) => {
    onSelect(selection.value);
  };

  const buildNumbersList = React.useCallback((n, x, list = [], s = 1) => {
    if (n > x) return list;
    list.push(n);
    return buildNumbersList(n + s, x, list);
  }, []);

  const options = useMemo(() => {
    const choices = buildNumbersList(min, max, [], step);
    return choices.map((num) => {
      let numString = num.toString();
      if (num < 10) {
        numString = `0${numString}`;
      }
      return { label: numString, value: numString };
    });
  }, [min, max, step, buildNumbersList]);

  const handleInputChange = (value_) => {
    const isValidInput = value_.length === 2 && isValidTime(value_, timeFormat);
    const filteredOptions = options.filter((o) => o.value.startsWith(value_));
    let selectionValue = '';

    if (timeFormat === 'HH' && Number.parseInt(value_, 10) > 23) {
      selectionValue = '23';
    } else if (!isValidInput && value_.length === 1 && filteredOptions.length < 2) {
      selectionValue = `0${value_}`;
    } else if (isValidInput) {
      selectionValue = value_;
    }

    if (selectionValue !== '') {
      onSelect(selectionValue);
    }
  };

  useEffect(() => {
    const selection = value && options && options.find((o) => o.value === value);
    setSelectedValue(() => selection);
  }, [value, options]);

  const PickerStyles = {
    container: (base) => ({
      ...base,
      width: '1.5rem',
      ...styles.container,
    }),
    control: (base, state) => ({
      ...base,
      border: 'none',
      '&:hover': { border: 'none', borderColor: 'transparent' },
      '&:active': { border: 'none', borderColor: 'transparent' },
      minHeight: 'uset',
      boxShadow: 'none',
      backgroundColor: 'transparent',
      justifyContent: 'flex-start',
      color: state.isFocused ? colors.StatusBlue : base.color,
      ...styles.control,
    }),
    singleValue: (base) => ({
      ...base,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      marginLeft: '0px',
      maxWidth: 'none',
      color: 'inherit',
      ...styles.singleValue,
    }),
    placeholder: (base) => ({
      ...base,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      marginLeft: '0px',
      maxWidth: 'none',
      ...styles.placeholder,
    }),
    input: () => ({
      position: 'absolute',
      input: {
        caretColor: 'transparent',
      },
    }),
    valueContainer: () => ({}),
    menu: (base) => ({
      ...base,
      width: 'fit-content',
      border: `1px solid ${theme === 'dark' ? colors.Grey70 : colors.Grey20}`,
      boxShadow: '0 0.5rem 0.5rem 0 rgba(0, 0, 0, 0.15)',
      zIndex: '1000',
      marginTop: '22px',
      marginBottom: '22px',
      ...styles.menu,
    }),
    menuList: (base) => ({
      ...base,
      '&::-webkit-scrollbar': {
        '-webkit-appearance': 'none',
        width: '4px',
        height: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '4px',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, .5)' : 'rgba(0, 0, 0, .5)',
        '-webkit-box-shadow':
          theme === 'dark' ? '0 0 1px rgba(0, 0, 0, .5)' : '0 0 1px rgba(255, 255, 255, .5)',
      },
    }),
  };

  const noOptionsMessageComp = () => <div>{noOptionsMessage}</div>;
  const innerClassNames = { option: () => 'time-picker__option' };

  return (
    <Select
      isMulti={false}
      options={options}
      value={selectedValue}
      placeholder={placeholder}
      className={className}
      classNames={innerClassNames}
      onFocus={onFocus}
      onChange={handleChange}
      onInputChange={handleInputChange}
      hideSelectedOptions={false}
      noOptionsMessage={noOptionsMessageComp}
      blurInputOnSelect
      openMenuOnFocus
      filterOption={filterOption}
      components={{ IndicatorsContainer: () => null }}
      innerRef={finalInputRef}
      styles={PickerStyles}
      data-testid={testId}
      {...extraProperties}
    />
  );
};

/*
 * Input/Output: time expression (xx:xx:xx)
 * Any timezone offset should be handled externally
 */
const TimePicker = ({
  value,
  withSeconds = false,
  onChange,
  styles = {},
  id,
  hourInputRef,
  secondsInputRef,
  onTabOrArrowBack,
  onTextComplete = () => null,
  // military = true, // TO DO : Add support for 12hr format?
}) => {
  const internalHourRef = useRef();
  const finalHourRef = hourInputRef || internalHourRef;

  const internalSecondsRef = useRef();
  const finalSecondsRef = secondsInputRef || internalSecondsRef;
  const minuteInputReference = useRef();

  const getTimeParts = React.useCallback((value_) => {
    const timeExp = /(\d\d:){1,2}(\d\d)/g;
    if (timeExp.test(value_) && value_.length <= 8) {
      return value_.split(':');
    }
    return [null, null, null];
  }, []);

  const initialFocus = {
    hours: false,
    minutes: false,
    seconds: false,
  };
  const [time, setTime] = useState();
  const [focus, setFocus] = useState({ ...initialFocus });

  useEffect(() => {
    if (value) {
      setTime(() => getTimeParts(value));
    }
  }, [value, getTimeParts]);

  const handleSelection = (inputType, value_) => {
    let newTime = [];
    if (inputType === 'h' && value_ !== time[0]) {
      newTime = [value_, (time && time[1]) || '00', (time && time[2]) || '00'];
      setFocus((old) => ({ ...old, hours: false, minutes: true }));
    }
    if (inputType === 'm' && value_ !== time[1]) {
      newTime = [(time && time[0]) || '00', value_, (time && time[2]) || '00'];
      setFocus((old) => ({ ...old, minutes: false, seconds: !!withSeconds }));
    }
    if (inputType === 's' && value_ !== time[2]) {
      newTime = [(time && time[0]) || '00', (time && time[1]) || '00', value_];
      setFocus((old) => ({ ...old, seconds: false }));
    }

    if (newTime.length > 0) {
      const newTimeString = newTime.join(':');
      onChange(newTimeString);
    }
  };

  const onKeyDown = (event) => {
    const arrowRight = event.key === 'ArrowRight';
    const shift = (event.key === 'Tab' && event.shiftKey) || event.key === 'ArrowLeft';
    if (shift && onTabOrArrowBack) {
      event.preventDefault();
      if (event.target === finalHourRef.current.inputRef) {
        onTabOrArrowBack();
      }

      if (event.target === minuteInputReference.current.inputRef) {
        finalHourRef.current.focus();
      }

      if (event.target === finalSecondsRef.current.inputRef) {
        minuteInputReference.current.focus();
      }
    } else if (arrowRight) {
      if (event.target === finalHourRef.current.inputRef) {
        minuteInputReference.current.focus();
      }

      if (event.target === minuteInputReference.current.inputRef) {
        finalSecondsRef.current.focus();
      }

      if (event.target === finalSecondsRef.current.inputRef) {
        onTextComplete();
      }
    } else if (event.key === 'Tab' && event.target === finalSecondsRef.current.inputRef) {
      event.preventDefault();
      onTextComplete();
    }
  };

  const hasTime = time && !time.includes(null);
  return (
    <div
      className="time-picker"
      onKeyDown={onKeyDown}
      id={id}
    >
      <div className="time-picker__time">
        <div className="time-picker__hours">
          <Picker
            timeFormat="HH"
            value={time && time[0]}
            placeholder="hh"
            className="time-picker__hours-input"
            onSelect={(value_) => handleSelection('h', value_)}
            onBlur={() => setFocus((old) => ({ ...old, hours: false }))}
            max={23}
            focus={focus.hours}
            onFocus={() =>
              setFocus((old) => ({
                ...old,
                hours: true,
                minutes: false,
                seconds: false,
              }))
            }
            inputRef={finalHourRef}
            styles={{ ...styles.hours }}
            testId="hours-select"
          />
        </div>
        <span>:</span>
        <div className="time-picker__minutes">
          <Picker
            timeFormat="mm"
            value={time && time[1]}
            placeholder={hasTime ? '00' : 'mm'}
            className="time-picker__minutes-input"
            onSelect={(value_) => handleSelection('m', value_)}
            onBlur={() => setFocus((old) => ({ ...old, minutes: false }))}
            max={59}
            focus={focus.minutes}
            onFocus={() =>
              setFocus((old) => ({
                ...old,
                minutes: true,
                hours: false,
                seconds: false,
              }))
            }
            inputRef={minuteInputReference}
            styles={{
              container: { width: hasTime ? '1.5rem' : '2.1rem' },
              ...styles.minutes,
            }}
            testId="minutes-select"
          />
        </div>
        {withSeconds && (
          <>
            <span>:</span>
            <div className="time-picker__seconds">
              <Picker
                timeFormat="ss"
                value={time && time[2]}
                placeholder="ss"
                className="time-picker__seconds-input"
                onSelect={(value_) => handleSelection('s', value_)}
                onBlur={() => setFocus((old) => ({ ...old, seconds: false }))}
                max={59}
                focus={focus.seconds}
                onFocus={() =>
                  setFocus((old) => ({
                    ...old,
                    seconds: true,
                    hours: false,
                    minutes: false,
                  }))
                }
                inputRef={finalSecondsRef}
                styles={{ ...styles.seconds }}
                testId="seconds-select"
              />
            </div>
          </>
        )}
      </div>
      <Icon
        className="time-picker__button"
        icon="he-time"
        size="s"
        onClick={() => setFocus((old) => ({ ...old, hours: true }))}
      />
    </div>
  );
};

export default TimePicker;
